import React, { useState } from 'react';

import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { themeStyle } from '@repo/ui/theming';
import { buildProfileImageUrl } from '@repo/utils/url';

import { useTeamAccountsFeature } from '../../../src/services/growthbook';
import { useUserStore, userStoreSelector } from '../../stores/userStore';
import Popover from '../Popover/Popover';
import { PopoverAlign } from '../Popover/types';

import AccountButton from './AccountButton/AccountButton';
import CreateNewTeamButton from './CreateNewTeamButton/CreateNewTeamButton';
import InactiveTeamAccountModal from './InactiveTeamAccountModal/InactiveTeamAccountModal';
import { ProfileImagesWrapper, SmallUserImageWrapper, Wrapper } from './styles';
import { popoverTheme } from './theme';
import { ProfileProps } from './types';
import UserNavigation from './UserNavigation/UserNavigation';

/**
 * render the profile in the header
 */
const Profile: React.FC<ProfileProps> = (props) => {
  const loginTeam = useUserStore(userStoreSelector.loginTeam);
  const swapMainUser = useUserStore(userStoreSelector.swapMainUser);
  const teamsEnabled = useTeamAccountsFeature();
  const [showModal, setShowModal] = useState(false);

  const handleChangeProfile = async (slug?: string): Promise<void> => {
    if (!slug) {
      return;
    }
    // if new profile is actual user
    if (
      props.profile.user.slug !== props.profile.slug &&
      props.profile.slug !== slug &&
      props.profile.user.slug === slug
    ) {
      await props.onBeforeProfileChange?.();
      await swapMainUser();
      props.onProfileChanged?.();
      return;
    }

    const team = props.profile.user?.teams
      ? props.profile.user.teams.find((team) => team.slug === slug)
      : null;

    if (team?.slug) {
      await props.onBeforeProfileChange?.();
      const response = await loginTeam({ slug: team.slug! });
      if ('error' in response) {
        setShowModal(true);
        return;
      }
      props.onProfileChanged?.();
    }
  };

  const content = (
    <>
      <Wrapper>
        <AccountButton
          isActualUser
          isActive={props.profile?.slug === props.profile.user.slug}
          user={props.profile.user ?? props.profile}
          onClick={(): void => {
            handleChangeProfile(
              props.profile.user?.slug ?? props.profile.slug ?? undefined
            );
          }}
        />

        {props.profile.user.teams &&
          props.profile.user.teams.map((team) => (
            <AccountButton
              key={team.slug}
              isActive={
                props.profile.slug ? props.profile.slug === team.slug : false
              }
              team={{
                ...team,
                profileImage: buildProfileImageUrl(team.profileImage),
              }}
              onClick={(): void => {
                handleChangeProfile(team.slug ?? undefined);
              }}
            />
          ))}

        {teamsEnabled && !props.profile.isTeam && (
          <CreateNewTeamButton
            theme={{
              padding: '17px 12px',
              height: 'auto',
              border: 'none',
              colorHover: themeStyle.varInkMain,
              fontSize: themeStyle.fontSize14,
              backgroundHover: themeStyle.varInkSuperLight,
            }}
            onClick={(): void => {
              props.onCreateTeam?.();
            }}
          />
        )}
      </Wrapper>
      <UserNavigation {...props} isTeam={props.profile.isTeam} />
    </>
  );

  const size = props.isSmall ? '25px' : '40px';

  const image = props.profile.isTeam ? (
    <ProfileImagesWrapper $disabled={props.disabled} size={size}>
      <ProfileImage profile={props.profile} size={size} />
      {props.profile.isTeam && (
        <SmallUserImageWrapper size={props.isSmall ? '12px' : '18px'}>
          <ProfileImage
            profile={props.profile.user}
            size={props.isSmall ? '10px' : '16px'}
          />
        </SmallUserImageWrapper>
      )}
    </ProfileImagesWrapper>
  ) : (
    <ProfileImage profile={props.profile} size={size} />
  );

  const popover = (
    <Popover
      hideTip
      align={PopoverAlign.End}
      content={content}
      dataTestId="profile"
      margin="15px 0 0 0"
      padding={-10}
      showHeader={false}
      theme={popoverTheme}
      useHover={true}
      width="290px"
      onClick={(): void => props.onClickItem(props.options['Profile'])}
    >
      {image}
    </Popover>
  );

  return (
    <>
      {props.disabled ? image : popover}
      <InactiveTeamAccountModal
        isOpen={showModal}
        onClose={(): void => setShowModal(false)}
      />
    </>
  );
};

export default Profile;
